import {Component, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import {IonicPage, NavController, Platform} from 'ionic-angular';
import {AlertProvider} from '../../providers/alert/alert';
import {ProofProvider} from '../../providers/proof/proof';

import {ProofTermsBeforeBeginPage} from '../proof-terms-before-begin/proof-terms-before-begin';

@IonicPage()
@Component({
  selector: 'page-peripheral-check',
  templateUrl: 'peripheral-check.html',
})
export class PeripheralCheckPage implements OnDestroy {

  @ViewChild('videoPreview') videoPreview: ElementRef;

  // Webcam state
  public webcamStatus: 'waiting' | 'testing' | 'success' | 'error' = 'waiting';
  public webcamErrorMessage: string = '';

  // Microphone state
  public microphoneStatus: 'waiting' | 'testing' | 'success' | 'error' = 'waiting';
  public microphoneErrorMessage: string = '';
  public audioLevel: number = 0;

  // Device info
  public browserInfo: string = '';
  public osInfo: string = '';
  public localIp: string = '';
  public publicIp: string = '';
  public memoryInfo: string = '';

  // Media streams
  private videoStream: MediaStream = null;
  private audioStream: MediaStream = null;
  private audioContext: AudioContext = null;
  private analyser: AnalyserNode = null;
  private audioCheckInterval: any = null;

  // Test state
  public isTestRunning: boolean = false;
  public allTestsPassed: boolean = false;
  public hasTestFailed: boolean = false;

  // Version info
  public version: string = '1.0';
  public currentDate: string = '';

  constructor(
    private navCtrl: NavController,
    private platform: Platform,
    private alertProvider: AlertProvider,
    private proofProvider: ProofProvider,
  ) {
    this.detectDeviceInfo();
    this.setCurrentDate();
  }

  ionViewWillLeave() {
    this.stopAllStreams();
  }

  ngOnDestroy() {
    this.stopAllStreams();
  }

  private setCurrentDate(): void {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const dayStr = day < 10 ? '0' + day : String(day);
    const monthStr = month < 10 ? '0' + month : String(month);
    this.currentDate = `${dayStr}/${monthStr}/${year}`;
  }

  private detectDeviceInfo(): void {
    // Browser detection
    const ua = navigator.userAgent;
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
      this.browserInfo = 'Google Chrome';
    } else if (ua.indexOf('Edg') > -1) {
      this.browserInfo = 'Microsoft Edge';
    } else if (ua.indexOf('Firefox') > -1) {
      this.browserInfo = 'Mozilla Firefox';
    } else if (ua.indexOf('Safari') > -1) {
      this.browserInfo = 'Apple Safari';
    } else {
      this.browserInfo = 'Navegador desconhecido';
    }

    // OS detection
    if (ua.indexOf('Windows NT 10') > -1 || ua.indexOf('Windows NT 11') > -1) {
      this.osInfo = 'Windows 10/11';
    } else if (ua.indexOf('Windows') > -1) {
      this.osInfo = 'Windows';
    } else if (ua.indexOf('Mac OS X') > -1) {
      this.osInfo = 'macOS';
    } else if (ua.indexOf('Linux') > -1) {
      this.osInfo = 'Linux';
    } else if (ua.indexOf('Android') > -1) {
      this.osInfo = 'Android';
    } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1) {
      this.osInfo = 'iOS';
    } else {
      this.osInfo = 'Sistema desconhecido';
    }

    // Memory info (if available)
    if ((navigator as any).deviceMemory) {
      this.memoryInfo = (navigator as any).deviceMemory + ' GB';
    } else {
      this.memoryInfo = 'N/A';
    }

    // Generate a local identifier
    this.localIp = this.generateLocalId();

    // Public IP will be fetched
    this.fetchPublicIp();
  }

  private generateLocalId(): string {
    // Generate a pseudo-local ID based on browser fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('test', 2, 2);
    const hash = canvas.toDataURL().slice(-20);
    return hash.replace(/[^a-z0-9]/gi, '').substring(0, 32) + '.local';
  }

  private fetchPublicIp(): void {
    // Try to fetch public IP using a free API
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => {
        this.publicIp = data.ip || 'N/A';
      })
      .catch(() => {
        this.publicIp = 'N/A';
      });
  }

  /**
   * Start all peripheral tests
   */
  public startTests(): void {
    if (this.isTestRunning) {
      return;
    }

    this.isTestRunning = true;
    this.allTestsPassed = false;

    // Reset states
    this.webcamStatus = 'testing';
    this.microphoneStatus = 'testing';
    this.webcamErrorMessage = '';
    this.microphoneErrorMessage = '';

    // Run tests in parallel
    Promise.all([
      this.testWebcam(),
      this.testMicrophone()
    ]).then(() => {
      this.isTestRunning = false;
      this.checkAllTestsPassed();
    }).catch(() => {
      this.isTestRunning = false;
      this.checkAllTestsPassed();
    });
  }

  /**
   * Test webcam
   */
  private testWebcam(): Promise<void> {
    return new Promise((resolve) => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.webcamStatus = 'error';
        this.webcamErrorMessage = 'API de mídia não disponível';
        resolve();
        return;
      }

      // First check if any video device exists
      this.checkDeviceExists('videoinput').then((hasCamera) => {
        if (!hasCamera) {
          this.webcamStatus = 'error';
          this.webcamErrorMessage = 'Nenhuma webcam encontrada';
          resolve();
          return;
        }

        navigator.mediaDevices.getUserMedia({video: true})
          .then((stream) => {
            this.videoStream = stream;

            // Connect stream to video element
            setTimeout(() => {
              if (this.videoPreview && this.videoPreview.nativeElement) {
                const video = this.videoPreview.nativeElement as HTMLVideoElement;
                video.srcObject = stream;
                video.play().catch(() => {});
              }
            }, 100);

            this.webcamStatus = 'success';
            resolve();
          })
          .catch((err) => {
            console.log('Webcam test failed:', err);
            this.webcamStatus = 'error';

            if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
              this.webcamErrorMessage = 'Nenhuma webcam encontrada';
            } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
              this.webcamErrorMessage = 'Permissão negada';
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
              this.webcamErrorMessage = 'Webcam em uso por outro aplicativo';
            } else {
              this.webcamErrorMessage = 'Erro ao acessar webcam';
            }

            resolve();
          });
      });
    });
  }

  /**
   * Check if a specific device type exists
   */
  private checkDeviceExists(deviceKind: string): Promise<boolean> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return Promise.resolve(true); // Assume exists if we can't check
    }

    return navigator.mediaDevices.enumerateDevices()
      .then((devices) => devices.some(device => device.kind === deviceKind))
      .catch(() => true); // Assume exists on error
  }

  /**
   * Test microphone
   */
  private testMicrophone(): Promise<void> {
    return new Promise((resolve) => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.microphoneStatus = 'error';
        this.microphoneErrorMessage = 'API de mídia não disponível';
        resolve();
        return;
      }

      // First check if any audio device exists
      this.checkDeviceExists('audioinput').then((hasMicrophone) => {
        if (!hasMicrophone) {
          this.microphoneStatus = 'error';
          this.microphoneErrorMessage = 'Nenhum microfone encontrado';
          resolve();
          return;
        }

        navigator.mediaDevices.getUserMedia({audio: true})
          .then((stream) => {
            this.audioStream = stream;

            // Set up audio analysis
            try {
              const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
              if (AudioContextClass) {
                this.audioContext = new AudioContextClass();
                this.analyser = this.audioContext.createAnalyser();
                this.analyser.fftSize = 256;

                const source = this.audioContext.createMediaStreamSource(stream);
                source.connect(this.analyser);

                // Start monitoring audio levels
                this.startAudioLevelMonitor();
              }
            } catch (err) {
              console.log('Audio context error:', err);
            }

            this.microphoneStatus = 'success';
            resolve();
          })
          .catch((err) => {
            console.log('Microphone test failed:', err);
            this.microphoneStatus = 'error';

            if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
              this.microphoneErrorMessage = 'Nenhum microfone encontrado';
            } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
              this.microphoneErrorMessage = 'Permissão negada';
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
              this.microphoneErrorMessage = 'Microfone em uso por outro aplicativo';
            } else {
              this.microphoneErrorMessage = 'Erro ao acessar microfone';
            }

            resolve();
          });
      });
    });
  }

  /**
   * Monitor audio levels for visual feedback
   */
  private startAudioLevelMonitor(): void {
    if (!this.analyser) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    this.audioCheckInterval = setInterval(() => {
      if (!this.analyser) return;

      this.analyser.getByteFrequencyData(dataArray);

      // Calculate average audio level
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const avg = sum / bufferLength;

      // Normalize to 0-100
      this.audioLevel = Math.min(100, Math.round((avg / 128) * 100));
    }, 100);
  }

  /**
   * Check if all tests passed
   */
  private checkAllTestsPassed(): void {
    this.allTestsPassed = this.webcamStatus === 'success' && this.microphoneStatus === 'success';
    this.hasTestFailed = this.webcamStatus === 'error' || this.microphoneStatus === 'error';
  }

  /**
   * Stop all media streams
   */
  private stopAllStreams(): void {
    // Stop audio monitoring
    if (this.audioCheckInterval) {
      clearInterval(this.audioCheckInterval);
      this.audioCheckInterval = null;
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
      this.analyser = null;
    }

    // Stop video stream
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }

    // Stop audio stream
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
  }

  /**
   * Proceed to next step
   */
  public proceed(): void {
    if (!this.allTestsPassed) {
      this.alertProvider.doAlert(
        'Por favor, complete todos os testes com sucesso antes de prosseguir.',
        'Atenção'
      );
      return;
    }

    // Stop streams before navigating
    this.stopAllStreams();

    // Navigate to next page
    this.navCtrl.push(ProofTermsBeforeBeginPage);
  }

  /**
   * Retry tests
   */
  public retryTests(): void {
    this.stopAllStreams();
    this.webcamStatus = 'waiting';
    this.microphoneStatus = 'waiting';
    this.audioLevel = 0;
    this.allTestsPassed = false;
    this.hasTestFailed = false;
  }

  /**
   * Go back to previous page
   */
  public goBack(): void {
    this.stopAllStreams();
    this.navCtrl.pop();
  }
}



// WEBPACK FOOTER //
// ./src/pages/peripheral-check/peripheral-check.ts